import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import Swiper from 'swiper';
import styles from './Gallery.module.sass';
import { galleryContent } from './Gallery.content';
import { Arrow } from './assets/Arrow';
import 'swiper/dist/css/swiper.min.css';

function buildFlatSlides(tabs) {
  return tabs.flatMap((tab, groupIndex) => {
    const list = Array.isArray(tab.images) ? tab.images : [tab.images];
    return list.map((src, i) => ({
      key: `${groupIndex}-${i}`,
      src,
      groupIndex,
      alt: tab.label,
    }));
  });
}

export function Gallery() {
  const tabs = galleryContent.tabs;
  const flatSlides = useMemo(() => buildFlatSlides(tabs), [tabs]);

  const containerRef = useRef(null);
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const [activeGallery, setActiveGallery] = useState(0);

  useEffect(() => {
    if (!containerRef.current || flatSlides.length === 0) return;

    swiperRef.current = new Swiper(containerRef.current, {
      loop: flatSlides.length > 1,
      slidesPerView: 1,
      speed: 700,
      autoplay: {
        delay: 4500,
        disableOnInteraction: false,
      },
      navigation: {
        prevEl: prevRef.current,
        nextEl: nextRef.current,
      },
      on: {
        slideChange: function () {
          const slide = flatSlides[this.realIndex];
          if (slide) setActiveGallery(slide.groupIndex);
        },
      },
    });

    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
        swiperRef.current = null;
      }
    };
  }, [flatSlides]);

  const handlePillClick = useCallback(
    (groupIndex) => {
      const start = flatSlides.findIndex((s) => s.groupIndex === groupIndex);
      if (start < 0) return;
      setActiveGallery(groupIndex);
      const sw = swiperRef.current;
      if (!sw) return;
      if (flatSlides.length > 1) sw.slideToLoop(start);
      else sw.slideTo(0, 0);
    },
    [flatSlides],
  );

  return (
    <section className={styles.gallery}>
      <div className={styles.gallery__overlay}>
        <div className={styles.gallery__left}>
          <div
            className={styles.gallery__title}
            dangerouslySetInnerHTML={{ __html: galleryContent.title }}
          />
          <div className={styles.gallery__pills}>
            {tabs.map((tab, index) => (
              <div
                key={tab.label}
                className={`${styles.gallery__pill} ${
                  index === activeGallery ? styles.active : ''
                }`}
                onClick={() => handlePillClick(index)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.gallery__right}>
          <div
            className={styles.gallery__title}
            dangerouslySetInnerHTML={{ __html: galleryContent.titleRight }}
          />
          <div className={styles.gallery__months}>
            {galleryContent.months.map((item) => {
              const label = typeof item === 'string' ? item : item.label;
              const href = typeof item === 'string' ? '#' : item.href;
              return (
                <a
                  key={label}
                  className={styles.gallery__month}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {label}
                </a>
              );
            })}
          </div>
        </div>

        <div className={styles.gallery__more}>больше фото +</div>
      </div>

      <div className={styles.gallery__sliderWrapper}>
        <div className={styles.gallery__nav}>
          <div ref={prevRef} className={styles.gallery__prev}>
            <Arrow />
          </div>
          <div ref={nextRef} className={styles.gallery__next}>
            <Arrow />
          </div>
        </div>

        <div className={`swiper-container ${styles.gallery__slider}`} ref={containerRef}>
          <div className="swiper-wrapper">
            {flatSlides.map((slide) => (
              <div key={slide.key} className={`swiper-slide ${styles.slide}`}>
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className={styles.slide__background}
                />
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className={styles.slide__image}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}